import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import HomeIcon from "@mui/icons-material/Home";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  PAGE_ROUTE_HOMEPAGE,
} from "@kivunova/kivufrontendcommon";
import { ROUTE_SUBSCRIPTIONS } from "../../constants/routes";
import AnimatedContainer from "../common/AnimatedContainer";

interface PlanData {
  planId: string;
  name: string;
  price: number;
  currency: string;
  billingCycle: string;
  description?: string;
  durationValue?: number;
  durationUnit?: "DAY" | "MONTH";
}

const formatSubscriptionDate = (date: Date): string =>
  date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

const addBillingDuration = (
  from: Date,
  durationValue: number,
  durationUnit: "DAY" | "MONTH",
): Date => {
  const result = new Date(from);
  if (durationUnit === "DAY") {
    result.setDate(result.getDate() + durationValue);
  } else {
    result.setMonth(result.getMonth() + durationValue);
  }
  return result;
};

const SubscriptionSuccess: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get plan data from navigation state
  const planData = (location.state as { plan?: PlanData })?.plan || null;

  const validFrom = planData ? new Date() : null;
  const validTill =
    planData?.durationValue != null && planData?.durationUnit
      ? addBillingDuration(
          validFrom!,
          planData.durationValue,
          planData.durationUnit,
        )
      : null;

  const handleGoToSubscriptions = () => {
    navigate(ROUTE_SUBSCRIPTIONS);
  };

  const handleGoToHome = () => {
    navigate(PAGE_ROUTE_HOMEPAGE);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #667eea 0%, #764ba2 25%, #6f49c9 50%, #5b36b0 75%, #4c2a85 100%)",
        position: "relative",
        overflow: "hidden",
        py: 6,
        px: { xs: 2, sm: 3, md: 4 },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Animated background elements */}
      <Box
        sx={{
          position: "absolute",
          top: "-30%",
          right: "-15%",
          width: "500px",
          height: "500px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)",
          animation: "float 20s ease-in-out infinite",
          "@keyframes float": {
            "0%, 100%": {
              transform: "translate(0, 0) rotate(0deg)",
            },
            "50%": {
              transform: "translate(-30px, -30px) rotate(180deg)",
            },
          },
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "-20%",
          left: "-10%",
          width: "400px",
          height: "400px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)",
          animation: "float 15s ease-in-out infinite reverse",
          "@keyframes float": {
            "0%, 100%": {
              transform: "translate(0, 0) rotate(0deg)",
            },
            "50%": {
              transform: "translate(30px, 30px) rotate(-180deg)",
            },
          },
        }}
      />

      <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
        <AnimatedContainer
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <Paper
            elevation={8}
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
              p: { xs: 3, sm: 4, md: 6 },
            }}
          >
            {/* Success Icon and Title */}
            <Box
              sx={{
                textAlign: "center",
                mb: 4,
              }}
            >
              <AnimatedContainer
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 100,
                    height: 100,
                    borderRadius: "50%",
                    background:
                      "linear-gradient(135deg, #4caf50 0%, #45a049 100%)",
                    mb: 3,
                    boxShadow: "0 8px 24px rgba(76, 175, 80, 0.3)",
                  }}
                >
                  <CheckCircleIcon
                    sx={{
                      fontSize: 60,
                      color: "white",
                    }}
                  />
                </Box>
              </AnimatedContainer>

              <Typography
                fontWeight="bold"
                sx={{
                  fontSize: {
                    xs: "1.25rem",
                    sm: "1.60rem",
                    md: "2.65rem",
                    lg: "2.75rem",
                  },
                  lineHeight: 1.3,
                  mb: 2,
                  color: "text.primary",
                  letterSpacing: { xs: "0.01em", md: "0.02em" },
                  textShadow: "0 2px 10px rgba(0,0,0,0.1)",
                }}
              >
                Subscription successful!
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: "1rem", sm: "1.125rem", md: "1.25rem" },
                  lineHeight: 1.7,
                  color: "text.secondary",
                  fontWeight: 400,
                  mb: 1,
                }}
              >
                Your subscription has been activated
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Thank you for subscribing. You now have access to all premium
                features.
              </Typography>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Subscription Details */}
            {planData && (
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ mb: 3, color: "text.primary" }}
                >
                  Subscription details
                </Typography>
                <List>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: BRAND_COLOR,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary="Plan"
                      secondary={planData.name}
                      primaryTypographyProps={{
                        fontWeight: "medium",
                        color: "text.secondary",
                      }}
                      secondaryTypographyProps={{
                        fontWeight: "bold",
                        color: "text.primary",
                      }}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: BRAND_COLOR,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary="Amount"
                      secondary={`${planData.currency} ${planData.price.toLocaleString()} ${planData.billingCycle}`}
                      primaryTypographyProps={{
                        fontWeight: "medium",
                        color: "text.secondary",
                      }}
                      secondaryTypographyProps={{
                        fontWeight: "bold",
                        color: "text.primary",
                      }}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: BRAND_COLOR,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary="Status"
                      secondary="Active"
                      primaryTypographyProps={{
                        fontWeight: "medium",
                        color: "text.secondary",
                      }}
                      secondaryTypographyProps={{
                        fontWeight: "bold",
                        color: "success.main",
                      }}
                    />
                  </ListItem>
                  {validFrom && (
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            backgroundColor: BRAND_COLOR,
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary="Valid from"
                        secondary={
                          validTill
                            ? `${formatSubscriptionDate(validFrom)} - ${formatSubscriptionDate(validTill)}`
                            : formatSubscriptionDate(validFrom)
                        }
                        primaryTypographyProps={{
                          fontWeight: "medium",
                          color: "text.secondary",
                        }}
                        secondaryTypographyProps={{
                          fontWeight: "bold",
                          color: "text.primary",
                        }}
                      />
                    </ListItem>
                  )}
                </List>
              </Box>
            )}

            <Divider sx={{ my: 4 }} />

            {/* Next Steps */}
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ mb: 2, color: "text.primary" }}
              >
                What&apos;s next?
              </Typography>
              <List>
                <ListItem sx={{ px: 0, py: 1 }}>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "success.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Access your subscription"
                    secondary="View and manage your subscription from the subscriptions page"
                    primaryTypographyProps={{
                      fontWeight: "medium",
                    }}
                  />
                </ListItem>
                <ListItem sx={{ px: 0, py: 1 }}>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "success.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Explore premium features"
                    secondary="Start using all the premium features included in your plan"
                    primaryTypographyProps={{
                      fontWeight: "medium",
                    }}
                  />
                </ListItem>
                <ListItem sx={{ px: 0, py: 1 }}>
                  <ListItemIcon>
                    <CheckCircleIcon sx={{ color: "success.main" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Get support"
                    secondary="Contact our support team if you need any assistance"
                    primaryTypographyProps={{
                      fontWeight: "medium",
                    }}
                  />
                </ListItem>
              </List>
            </Box>

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
                mt: 4,
              }}
            >
              <Button
                variant="contained"
                fullWidth
                onClick={handleGoToSubscriptions}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  backgroundColor: BRAND_COLOR,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: "bold",
                  fontSize: "1rem",
                  "&:hover": {
                    backgroundColor: BRAND_HOVER,
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 16px rgba(111, 73, 201, 0.3)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                View my subscriptions
              </Button>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleGoToHome}
                startIcon={<HomeIcon />}
                sx={{
                  borderColor: BRAND_COLOR,
                  color: BRAND_COLOR,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: "bold",
                  textTransform: "none",
                  fontSize: "1rem",
                  "&:hover": {
                    borderColor: BRAND_HOVER,
                    backgroundColor: "rgba(111, 73, 201, 0.04)",
                    transform: "translateY(-2px)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Back to home
              </Button>
            </Box>
          </Paper>
        </AnimatedContainer>
      </Container>
    </Box>
  );
};

export default SubscriptionSuccess;
